"use strict";
/**
 * Order Placement
 *
 * Handles the core order placement flow:
 * - Validates manual price selection when in manual price mode
 * - Temporarily overrides slippage settings for baseball mode buttons
 * - Routes to the unified placeNewOrder execution pipeline
 * - Fires additional market orders for assigned baseball buttons
 * - Shows success/error notifications and handles secret-key re-auth
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.hasSelectedManualPrice = hasSelectedManualPrice;
exports.resolveParallelBuyManualPrice = resolveParallelBuyManualPrice;
exports.handlePlaceOrder = handlePlaceOrder;
exports.handlePlaceAdditionalOnlyOrder = handlePlaceAdditionalOnlyOrder;
const state_1 = require("../../state");
const ui_1 = require("../../ui");
const orders_1 = require("../../orders");
const slippage_utils_1 = require("../../orders/slippage-utils");
const click_id_1 = require("../../orders/click-id");
const price_sample_arm_1 = require("../../orders/price-sample-arm");
const api_1 = require("../../api");
const additional_markets_service_1 = require("../../services/additional-markets-service");
const permissions_1 = require("../../permissions");
const displayed_ask_history_1 = require("../../orders/displayed-ask-history");
/**
 * Check whether the user has actively selected a manual price for the given
 * side. Returns `true` when the manual-price checkbox/slider for `side` is
 * marked as "selected" in the trading settings.
 */
function hasSelectedManualPrice(side) {
    const { tradingSettings } = (0, state_1.getState)();
    return side === "yes"
        ? tradingSettings.manualYesPriceSelected === true
        : tradingSettings.manualNoPriceSelected === true;
}
function resolveParallelBuyManualPrice(side, slippageOverride, manualPriceOverride) {
    if (manualPriceOverride !== undefined) {
        return Math.max(1, Math.min(99, Math.round(manualPriceOverride)));
    }
    const state = (0, state_1.getState)();
    const { tradingSettings, cachedBidAsk } = state;
    if (tradingSettings.manualPriceMode === true &&
        !(0, ui_1.isSportLayoutActive)(tradingSettings)) {
        return side === "yes"
            ? tradingSettings.manualYesPrice
            : tradingSettings.manualNoPrice;
    }
    // Track the primary order's reference. Under Dly the primary prices off the
    // ask displayed ~one reaction time ago, so reading the click-instant book
    // here would send the same click out at two different limits. Falls back to
    // the live cache when the ring has nothing for this market yet.
    const seenAsk = tradingSettings.useDelayedPrice === true && (0, permissions_1.canDelayedPrice)()
        ? (0, displayed_ask_history_1.getDisplayedAsk)(state.selectedMarket?.id ?? "", side)?.price
        : undefined;
    const askDecimal = seenAsk ?? (side === "yes" ? cachedBidAsk?.yesAsk : cachedBidAsk?.noAsk);
    if (!askDecimal || askDecimal <= 0) {
        return null;
    }
    const effectiveSlippage = slippageOverride !== undefined
        ? slippageOverride
        : (0, slippage_utils_1.getEffectiveSlippage)(tradingSettings, side);
    const askCents = Math.round(askDecimal * 100);
    return Math.max(1, Math.min(99, Math.round(askCents + effectiveSlippage)));
}
/**
 * Place an order for the currently selected market.
 *
 * Flow:
 * 1. Validate that a market is selected.
 * 2. If manual-price mode is active, validate that a price has been picked.
 * 3. Optionally apply a one-shot slippage override (used by baseball buttons).
 * 4. Delegate to `placeNewOrder` which routes to the correct provider
 *    (Kalshi / Polymarket / PM-US).
 * 5. Fire any additional market orders assigned to the triggering button.
 * 6. Show a success or error toast and restore original settings if overridden.
 *
 * @param side  - Which side of the market ("yes" or "no")
 * @param action - Whether this is a "buy" or "sell"
 * @param slippageOverride - Optional one-time slippage percentage (sport modes)
 * @param triggerButtonId - Optional order-button ID that triggered this order
 */
async function handlePlaceOrder(side, action, slippageOverride, triggerButtonId, manualPriceOverride) {
    const state = (0, state_1.getState)();
    // $0-cap tier (level 0) can't trade at all: the server rejects every order
    // (buys AND sells), so block here up front — including the additional/parallel
    // fan-outs — rather than fire a doomed request. The buy/sell buttons are also
    // disabled by the gate, so this only trips on an edge path.
    if (!(0, permissions_1.canPlaceBets)()) {
        (0, ui_1.showOrderError)("Your account can't place orders");
        return;
    }
    // The sell buttons are hidden for tiers without it, so this only catches an
    // edge path — e.g. a button armed before a mid-session demotion.
    if (action === "sell" && !(0, permissions_1.canSellOrders)()) {
        (0, ui_1.showOrderError)("Selling is not permitted for your account");
        return;
    }
    // One id for this whole click, minted before any branch that can fire an
    // order so the primary and every fan-out request carry the same one.
    const clickId = (0, click_id_1.newClickId)();
    const primaryOrdersEnabled = state.tradingSettings.primaryOrdersEnabled !== false;
    // Parallel-markets is a trusted-tier feature (server-enforced). Suppress the
    // fan-out entirely when the tier lacks it so stale localStorage selections
    // can't fire doomed 403s. The assign UI is hidden too, so a denied user
    // shouldn't reach here in normal flow.
    const parallelMarkets = action === "buy" && (0, permissions_1.canParallelMarkets)()
        ? (state.parallelMarkets ?? [])
        : [];
    const assignments = triggerButtonId && action === "buy"
        ? (0, additional_markets_service_1.getAssignmentsForButton)(triggerButtonId)
        : [];
    const sharedParallelManualPrice = parallelMarkets.length > 0
        ? resolveParallelBuyManualPrice(side, slippageOverride, manualPriceOverride)
        : null;
    // Context for the price-sample button: which click, the market ON SCREEN,
    // and how many requests it fans into (the server's write barrier waits for
    // that many). Only a buy can arm, so only a buy is registered.
    const sampleVenue = (0, price_sample_arm_1.toSampleVenue)(state.dataSource);
    if (action === "buy" && sampleVenue && state.selectedMarket) {
        (0, price_sample_arm_1.registerClick)({
            clickId,
            marketId: state.selectedMarket.id,
            venue: sampleVenue,
            expectedRequests: (primaryOrdersEnabled ? 1 : 0) +
                parallelMarkets.length +
                assignments.length,
        });
    }
    if (!primaryOrdersEnabled) {
        if (action !== "buy") {
            (0, ui_1.showOrderError)("Primary market orders are disabled");
            return;
        }
        if (assignments.length === 0) {
            (0, ui_1.showOrderError)("Primary market orders are disabled and this button has no additional market assignments");
            return;
        }
        if (parallelMarkets.length > 0 && sharedParallelManualPrice === null) {
            (0, ui_1.showOrderError)("Parallel markets require a current primary ask price before buying.");
            return;
        }
        fireAdditionalMarketOrders(assignments, clickId);
        if (parallelMarkets.length > 0 && sharedParallelManualPrice !== null) {
            fireParallelMarketOrders(parallelMarkets, side, sharedParallelManualPrice, state.tradingSettings.dollarAmount, clickId);
        }
        return;
    }
    if (!state.selectedMarket) {
        (0, ui_1.showOrderError)("No market selected");
        return;
    }
    // When a sport layout is showing (baseball OR basketball, and not overridden
    // by forceDefaultButtons) per-button prices are used, so skip the global
    // manual-price selection check. Only enforce it in pure manual-price mode.
    const sportLayoutActive = (0, ui_1.isSportLayoutActive)(state.tradingSettings);
    if (state.tradingSettings.manualPriceMode === true && !sportLayoutActive) {
        if (!hasSelectedManualPrice(side)) {
            (0, ui_1.showOrderError)(`Select a manual ${side.toUpperCase()} price before placing an order`);
            return;
        }
    }
    // If slippage override is provided (and no per-button price), temporarily set slippage
    let originalSettings = null;
    if (slippageOverride !== undefined && manualPriceOverride === undefined) {
        originalSettings = { ...state.tradingSettings };
        (0, state_1.updateState)({
            tradingSettings: {
                ...state.tradingSettings,
                independentSlippage: false,
                slippageTolerancePercent: slippageOverride,
            },
        });
    }
    if (parallelMarkets.length > 0 && sharedParallelManualPrice === null) {
        (0, ui_1.showOrderError)("Parallel markets require a current primary ask price before buying.");
        return;
    }
    let orderOptions = { clickId };
    if (parallelMarkets.length > 0 && sharedParallelManualPrice !== null) {
        orderOptions = {
            ...orderOptions,
            manualPrice: sharedParallelManualPrice,
            dollarAmount: state.tradingSettings.dollarAmount,
        };
    }
    else if (manualPriceOverride !== undefined) {
        orderOptions = { ...orderOptions, manualPrice: manualPriceOverride };
    }
    try {
        // Start primary order first, then immediately fan out additional/parallel
        // orders without waiting for the primary response.
        const primaryOrderPromise = (0, orders_1.placeNewOrder)(state.selectedMarket.id, side, action, undefined, orderOptions);
        if (assignments.length > 0) {
            fireAdditionalMarketOrders(assignments, clickId);
        }
        if (parallelMarkets.length > 0 && sharedParallelManualPrice !== null) {
            fireParallelMarketOrders(parallelMarkets, side, sharedParallelManualPrice, state.tradingSettings.dollarAmount, clickId);
        }
        const order = await primaryOrderPromise;
        (0, ui_1.showOrderStatus)(`Order placed successfully! ID: ${order.order_id}`, "success");
        // Hide status after 3 seconds
        setTimeout(() => (0, ui_1.hideOrderStatus)(), 3000);
    }
    catch (error) {
        console.error("Failed to place order:", error);
        // Check if error is due to invalid secret key
        if ((0, api_1.isSecretKeyError)(error)) {
            (0, ui_1.showReAuthModal)();
            (0, ui_1.showOrderError)("Invalid secret key. Please re-enter it.");
        }
        else {
            (0, ui_1.showOrderError)(error instanceof Error ? error.message : "Failed to place order");
        }
    }
    finally {
        // Restore original settings if they were overridden
        if (originalSettings) {
            (0, state_1.updateState)({ tradingSettings: originalSettings });
        }
    }
}
/**
 * Fire only additional market orders assigned to a baseball button.
 * Used by dedicated EXT buttons that are intentionally disconnected
 * from the primary market order flow.
 */
function handlePlaceAdditionalOnlyOrder(triggerButtonId) {
    const assignments = (0, additional_markets_service_1.getAssignmentsForButton)(triggerButtonId);
    if (assignments.length === 0) {
        return;
    }
    // Its own click: this button is deliberately disconnected from the primary
    // order flow, so its orders are not part of any other click's fan-out.
    const clickId = (0, click_id_1.newClickId)();
    const state = (0, state_1.getState)();
    const sampleVenue = (0, price_sample_arm_1.toSampleVenue)(state.dataSource);
    // The operator is still looking at the selected market's book even though
    // this click traded none of it; the sample endpoint reads every one of these
    // legs server-side (no primary leg → no market guard).
    if (sampleVenue && state.selectedMarket) {
        (0, price_sample_arm_1.registerClick)({
            clickId,
            marketId: state.selectedMarket.id,
            venue: sampleVenue,
            expectedRequests: assignments.length,
        });
    }
    fireAdditionalMarketOrders(assignments, clickId);
}
/**
 * Fire additional market orders for all assignments.
 * Each order uses the assignment's specific dollar amount, side, and manual
 * price without mutating the shared trading settings.
 *
 * These orders are fire-and-forget: they run in parallel and errors
 * are logged but don't block the user.
 */
function fireAdditionalMarketOrders(assignments, clickId) {
    // Additional-markets is a trusted-tier feature (server-enforced). Suppress
    // the fan-out entirely when the tier lacks it so stale localStorage
    // assignments can't fire doomed 403s. The assign UI is hidden too, so a
    // denied user shouldn't reach here in normal flow. Single funnel for every
    // fan-out call site.
    if (!(0, permissions_1.canAdditionalMarkets)()) {
        return;
    }
    for (const assignment of assignments) {
        fireOneAdditionalOrder(assignment, clickId).catch((err) => {
            console.error(`[Additional Market] Failed to place order for ${assignment.marketTicker}:`, err);
        });
    }
}
function fireParallelMarketOrders(parallelMarkets, side, manualPrice, dollarAmount, clickId) {
    // Trusted-tier feature (server-enforced). Suppress the fan-out when the tier
    // lacks it so stale localStorage selections can't fire doomed 403s. Single
    // funnel for every fan-out call site (mirrors fireAdditionalMarketOrders).
    if (!(0, permissions_1.canParallelMarkets)()) {
        return;
    }
    for (const selection of parallelMarkets) {
        // An inverted leg's other side is the same real-world outcome as the
        // clicked side, so the shared limit applies unchanged.
        const legSide = selection.invert ? (side === "yes" ? "no" : "yes") : side;
        (0, orders_1.placeNewOrder)(selection.marketId, legSide, "buy", selection.venue, {
            manualPrice,
            dollarAmount,
            parallelMarket: true,
            clickId,
            ...(selection.novigOutcomeIds ? { novigOutcomeIds: selection.novigOutcomeIds } : {}),
        }).catch((err) => {
            console.error(`[Parallel Market] Failed to place order for ${selection.marketId}:`, err);
        });
    }
}
/**
 * Place a single additional market order.
 *
 * The assignment's manual price is passed directly as an explicit option so
 * that createOrderRequest takes the options.manualPrice fast-path and sets
 * manualLimitPrice on the wire, regardless of the current global baseball /
 * manual-price mode flags. The dollar amount is also passed as an explicit
 * override so concurrent additional orders do not touch global state.
 *
 * @param assignment - The additional market assignment to place
 */
async function fireOneAdditionalOrder(assignment, clickId) {
    // Route to the correct venue: use the assignment's venue when stored,
    // otherwise fall back to the current global data source.
    // Always pass explicit per-order overrides so the backend receives
    // manualLimitPrice and uses the assignment-specific dollar amount.
    const order = await (0, orders_1.placeNewOrder)(assignment.marketTicker, assignment.side, "buy", assignment.venue, {
        manualPrice: assignment.manualPrice,
        dollarAmount: assignment.dollarAmount,
        additionalMarket: true,
        clickId,
        ...(assignment.novigOutcomeIds ? { novigOutcomeIds: assignment.novigOutcomeIds } : {}),
    });
    console.log(`[Additional Market] Order placed for ${assignment.marketTicker}: ${order.order_id}`);
}
